import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons';
import AccountsSummary from '../../../../pages/financial-institution/accounts-summary';
import { cn } from '../../../../../utils/styles';
import Icons, { IconProps } from '../../../../../components/Common/Icons/Icons';
import HoverPopover from '../../../../../components/Common/UI/Popover/HoverPopover';
import Statements from '../../../../pages/financial-institution/statements';
import InnerApplication from '../../../../pages/financial-institution/inner-application';
import { useInstitutionMenuContextProvider } from '../../../../context/institutionMenu';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../../../../../components/Common/UI/Drawer';
import { ScrollArea } from '../../../../../components/Common/UI/ScrollArea';
import { MenuPopoverContent, Popover, PopoverTrigger } from '../../../../../components/Common/UI/Popover';
import { LogoFromSettings } from '../../../Common/Logo';
import { MenuRoute } from '../../../../../components/Layout/Menu';

function UserProfileIcon(props: IconProps) {
  return (
    <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_1036_539)">
        <circle opacity="0.1" cx="20.5" cy="20.5" r="20" fill="#EF9F00" stroke="#EF9F00" />
        <path
          /* eslint-disable-next-line max-len */
          d="M24.21 21.21C25.1904 20.4387 25.906 19.3809 26.2572 18.1839C26.6085 16.987 26.5779 15.7103 26.1698 14.5315C25.7617 13.3527 24.9963 12.3304 23.9801 11.6069C22.9639 10.8833 21.7474 10.4945 20.5 10.4945C19.2525 10.4945 18.0361 10.8833 17.0199 11.6069C16.0037 12.3304 15.2383 13.3527 14.8302 14.5315C14.4221 15.7103 14.3915 16.987 14.7428 18.1839C15.094 19.3809 15.8096 20.4387 16.79 21.21C15.1101 21.883 13.6443 22.9994 12.5489 24.4399C11.4535 25.8805 10.7696 27.5913 10.57 29.39C10.5556 29.5213 10.5671 29.6542 10.604 29.7811C10.6409 29.9079 10.7025 30.0263 10.7851 30.1293C10.952 30.3375 11.1948 30.4708 11.46 30.5C11.7252 30.5292 11.9912 30.4518 12.1993 30.2849C12.4075 30.118 12.5408 29.8752 12.57 29.61C12.7896 27.6552 13.7217 25.8498 15.1882 24.5388C16.6547 23.2278 18.5529 22.503 20.52 22.503C22.4871 22.503 24.3852 23.2278 25.8518 24.5388C27.3183 25.8498 28.2504 27.6552 28.47 29.61C28.4972 29.8557 28.6144 30.0827 28.7991 30.247C28.9838 30.4114 29.2228 30.5015 29.47 30.5H29.58C29.8421 30.4698 30.0817 30.3373 30.2466 30.1313C30.4114 29.9252 30.4881 29.6624 30.46 29.4C30.2595 27.5962 29.5719 25.881 28.4708 24.4382C27.3698 22.9954 25.8969 21.8795 24.21 21.21ZM20.5 20.5C19.7089 20.5 18.9355 20.2654 18.2777 19.8259C17.6199 19.3864 17.1072 18.7616 16.8045 18.0307C16.5017 17.2998 16.4225 16.4956 16.5769 15.7196C16.7312 14.9437 17.1122 14.231 17.6716 13.6716C18.231 13.1122 18.9437 12.7312 19.7196 12.5769C20.4956 12.4225 21.2998 12.5017 22.0307 12.8045C22.7616 13.1072 23.3863 13.6199 23.8259 14.2777C24.2654 14.9355 24.5 15.7089 24.5 16.5C24.5 17.5609 24.0786 18.5783 23.3284 19.3284C22.5783 20.0786 21.5609 20.5 20.5 20.5Z"
          fill="#EF9F00"
        />
      </g>
      <defs>
        <clipPath id="clip0_1036_539">
          <rect width="41" height="41" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

interface HeaderItem {
  label: React.ReactNode;
  path: string;
  children?: HeaderItem[];
}

const headerItems: HeaderItem[] = [
  {
    label: 'Account Summary',
    path: 'financial-institution',
  },
  {
    label: 'Manage Money',
    path: '#',
  },
  {
    label: 'Move Money',
    path: '#',
  },
  {
    label: 'Additional Services',
    path: '#',
    children: [
      { path: 'financial-institution/statements', label: 'Statements' },
      { path: '#', label: 'Text Banking & Alerts' },
      { path: '#', label: 'Card Management' },
      { path: '#', label: 'My TurboTax' },
      { path: '#', label: 'Chat Offline' },
      { path: '#', label: 'Schedule Appointment' },
      { path: '#', label: 'Fresh Desk' },
    ],
  },
  {
    label: 'Secure Forms',
    path: '#',
  },
];

export const financialInstitutionRoutes: MenuRoute[] = [
  {
    name: 'Guidance: Accounts summary',
    route: {
      path: '/guidance/financial-institution',
      element: <AccountsSummary />,
    },
    children: [
      {
        name: 'Statements',
        route: {
          path: 'statements',
          element: <Statements />,
        },
      },
      {
        name: 'Application',
        route: {
          path: 'application',
          element: <InnerApplication />, // Displays guidance in iframe
        },
      },
    ],
  },
];

function DesktopNavigation() {
  const { pathname } = useLocation();

  return (
    <div
      className={cn([
        'hidden lg:flex justify-center items-center max-w-[1200px] mx-auto px-2 h-34 flex-wrap',
        'md:flex-nowrap md:h-20 md:justify-between',
      ])}
    >
      <div>
        <Link to="/guidance/financial-institution" className="flex items-center gap-2">
          {/* <div className="h-[26px] w-[26px] bg-[#EF9F00]" />
          <span className="font-bold text-[18px] tracking-tight">FINANCIAL INSTITUTION</span> */}
          <LogoFromSettings size="sm" />
        </Link>
      </div>

      <div className="flex gap-4 items-center text-addition1-50 child-hover:text-addition1-60">
        {headerItems.map(({ label, path, children }) => {
          if (children?.length) {
            return (
              <HoverPopover
                key={`${path}${label}`}
                size="auto"
                label={
                  <div
                    className={cn('flex items-center gap-2 h-[35px]', {
                      'font-bold text-[#202020]': children.some(child => pathname.endsWith(child.path)),
                    })}
                  >
                    {label}
                    <ChevronDownIcon />
                  </div>
                }
                align="center"
                popoverClassName="p-0 w-max mt-[-4px]"
              >
                <div className="flex flex-col">
                  {children
                    .filter(item => item.path !== pathname)
                    .map(item => (
                      <Link
                        key={`${item.path}${item.label}`}
                        to={item.path && item.path !== '#' ? `/guidance/${item.path}` : item.path}
                        className="px-2 py-1 hover:bg-muted"
                      >
                        {item.label}
                      </Link>
                    ))}
                </div>
              </HoverPopover>
            );
          }

          return (
            <Link
              key={`${path}${label?.toString()}`}
              to={path && path !== '#' ? `/guidance/${path}` : path}
              className={cn({
                'font-bold text-[#202020]': pathname.endsWith(path),
              })}
            >
              {label}
            </Link>
          );
        })}
      </div>

      <div className="flex gap-4 items-center text-addition1-60 child-hover:text-addition1-70">
        <Link to="#">
          <Icons.SettingsFilled />
        </Link>

        <Link to="#">
          <Icons.Help />
        </Link>

        <Link to="#">
          <UserProfileIcon />
        </Link>
      </div>
    </div>
  );
}

function MobileNavigation() {
  const { pathname } = useLocation();
  const { isMenuOpen, toggleMenu, handleMenu } = useInstitutionMenuContextProvider();

  return (
    <div className={cn(['lg:hidden flex flex-col justify-center mx-auto px-2 h-16 flex-wrap'])}>
      <div className="flex gap-[20px] justify-between items-center">
        <Icons.HamburgerIcon className="relative cursor-pointer align-top lg:hidden" onClick={toggleMenu} />
        <Link to="/guidance/financial-institution" className="flex items-center gap-2">
          <LogoFromSettings size="xs" />
          {/* <div className="h-[26px] w-[26px] bg-[#EF9F00]" /> */}
          {/* <span className="font-bold text-[14px] tracking-tight">FINANCIAL INSTITUTION</span> */}
        </Link>
        <Popover>
          <PopoverTrigger className="block">
            <Icons.More className="text-secondary2 rotate-90" />
          </PopoverTrigger>
          <MenuPopoverContent className="!bg-white min-w-8 flex gap-4 flex-col text-addition1-60 child-hover:text-addition1-70">
            <Link to="#">
              <Icons.SettingsFilled />
            </Link>

            <Link to="#">
              <Icons.Help />
            </Link>

            <Link to="#">
              <UserProfileIcon className="size-[24px]" />
            </Link>
          </MenuPopoverContent>
        </Popover>
      </div>

      <Drawer direction="top" open={isMenuOpen} onOpenChange={handleMenu}>
        <DrawerContent direction="right" className="h-max w-full max-w-full">
          <ScrollArea className="h-auto">
            <DrawerHeader>
              <div className="flex justify-between items-center">
                <DrawerClose asChild>
                  <Cross1Icon className="cursor-pointer w-[28px] h-[28px]" />
                </DrawerClose>
                <DrawerTitle className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <LogoFromSettings size="xs" defaultSrc="/guidance/logo-mini.png" />
                    {/* <div className="h-[26px] w-[26px] bg-[#EF9F00]" /> */}
                    {/* <span className="font-bold text-[14px] tracking-tight">FINANCIAL INSTITUTION</span> */}
                  </div>
                </DrawerTitle>
              </div>
            </DrawerHeader>

            <div className="main-content p-4" data-vaul-no-drag>
              <div className="flex flex-col gap-2 items-center text-addition1-50 child-hover:text-addition1-60">
                {headerItems.map(({ label, path, children }) => {
                  if (children?.length) {
                    return (
                      <HoverPopover
                        key={`${path}${label}`}
                        size="auto"
                        label={
                          <div
                            className={cn('flex items-center gap-2 h-[35px]', {
                              'font-bold text-[#202020]': children.some(child => pathname.endsWith(child.path)),
                            })}
                          >
                            {label}
                            <ChevronDownIcon />
                          </div>
                        }
                        align="center"
                        popoverClassName="p-0 w-max mt-[-4px]"
                      >
                        <div className="flex flex-col">
                          {children
                            .filter(item => item.path !== pathname)
                            .map(item => (
                              <Link
                                // data-vaul-no-drag
                                key={`${item.path}${item.label}`}
                                to={item.path && item.path !== '#' ? `/guidance/${item.path}` : item.path}
                                className="px-2 py-1 hover:bg-muted"
                              >
                                {item.label}
                              </Link>
                            ))}
                        </div>
                      </HoverPopover>
                    );
                  }

                  return (
                    <Link
                      key={`${path}${label?.toString()}`}
                      to={path && path !== '#' ? `/guidance/${path}` : path}
                      className={cn('w-full hover:bg-slate-100 text-center py-2', {
                        'font-bold text-[#202020]': pathname.endsWith(path),
                      })}
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function InstitutionHeader() {
  return (
    <header className="fixed md:sticky top-0 z-50 w-full bg-white shadow">
      <DesktopNavigation />
      <MobileNavigation />
    </header>
  );
}

export default InstitutionHeader;
